import { BreakpointObserver } from "@angular/cdk/layout";
import { LocationStrategy } from "@angular/common";
import { Injectable } from "@angular/core";
import { NavigationExtras, Router } from "@angular/router";
import { BREAKPOINTS, MediaQueryBuilder } from "@core/constants";
import { BehaviorSubject, Observable } from "rxjs";

@Injectable({
  providedIn: "root"
})
export class SidebarService {
  /**
   * Define media query for sidebar to be expanded.
   * Must be set before first call to getSidebarState to have an effect.
   */
  sidebarExpansionMediaQuery = MediaQueryBuilder.is().gt(BREAKPOINTS.M).query;

  private sidebarState$: BehaviorSubject<boolean>;
  private sidebarVisibility$ = new BehaviorSubject<boolean>(true);
  private serviceRequestPendingAlertSubject = new BehaviorSubject<boolean>(
    false
  );

  constructor(
    private router: Router,
    private locationStrategy: LocationStrategy,
    private breakpointObserver: BreakpointObserver
  ) {}

  setSidebarState(open: boolean) {
    this.initSidebarStateBehavior();
    this.sidebarState$.next(open);
    if (typeof window["BCIPortal"] !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("bci.portal.navigation.toggle", {
          detail: { closed: !open }
        })
      );
    }
  }

  setServiceRequestPendingAlertState(value: boolean) {
    this.serviceRequestPendingAlertSubject.next(value);
  }

  getServiceRequestPendingAlertState$(): Observable<boolean> {
    return this.serviceRequestPendingAlertSubject.asObservable();
  }

  setSidebarVisibility(visible: boolean) {
    this.sidebarVisibility$.next(visible);
  }

  getSidebarState(): Observable<boolean> {
    this.initSidebarStateBehavior();
    return this.sidebarState$.asObservable();
  }

  get isSidebarOpen() {
    this.initSidebarStateBehavior();
    return this.sidebarState$.getValue();
  }

  getSidebarVisibility(): Observable<boolean> {
    return this.sidebarVisibility$.asObservable();
  }

  private initSidebarStateBehavior() {
    if (!this.sidebarState$) {
      this.sidebarState$ = new BehaviorSubject<boolean>(
        this.breakpointObserver.isMatched(this.sidebarExpansionMediaQuery)
      );
      this.breakpointObserver
        .observe(this.sidebarExpansionMediaQuery)
        .subscribe((result) => this.setSidebarState(result.matches));
    }
  }

  /**
   * Navigates to the URL either internally (with Angular Router) or externally (window.location).
   * If there is not http protocol or if the origin is the same and the route is part of the router config, navigate internally.
   * Else, navigate externally.
   * @param url Absolute URL or internal route.
   * @param extras {@link NavigationExtras}
   * @returns The {@link router} navigate return
   */
  navigateToUrl(url: string, extras?: NavigationExtras): Promise<boolean> {
    let urlObject;
    try {
      urlObject = new URL(url);
    } catch (e) {
      /*assuming TypeError, Invalid URL -> navigate internally directly to URL*/
      return this.router.navigate([url], extras);
    }
    if (urlObject) {
      let baseHref = this.locationStrategy.getBaseHref();
      baseHref = baseHref.endsWith("/") ? baseHref : `${baseHref}/`;
      if (
        urlObject.origin === window.location.origin &&
        urlObject.pathname.startsWith(baseHref)
      ) {
        return this.router.navigate([urlObject.pathname], extras);
      } else {
        window.location.href = urlObject.href;
      }
    }
  }
}
